import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { BeatLoader } from 'react-spinners';
import { FaPhone, FaArrowRight } from 'react-icons/fa';
import { getRegistrationDetailByActivityIdAndPhoneNumber } from '../../api/registration';
import Cookies from 'js-cookie';
import { resolvePageContext } from '../../utils/fanMemberUtils';
import { getFanMemberBrandTheme } from '../../config/fanMembersConfig';

const LoginPage = ({ fixedActivityType, fixedId, fixedFanSlug }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const pageContext = resolvePageContext({ fixedFanSlug, fixedActivityType, fixedId, params });
  const { activityId: id, activityType, isFanMemberRoute, navigateTo, fanMember } = pageContext;
  const brand = getFanMemberBrandTheme(fanMember);

  // Get the intended destination from location state
  const from = location.state?.from?.pathname || null;

  useEffect(() => {
    // Add entrance animation
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await getRegistrationDetailByActivityIdAndPhoneNumber(id, phoneNumber);
      if (response.success) {
        setShowSuccess(true);
        Cookies.set('registrationId', response.data.id);
        Cookies.set('phoneNumber', response.data.user.phoneNumber);
        setTimeout(() => {
          // If user was redirected from a protected route, go back there
          if (from) {
            navigate(from, { replace: true });
          } else {
            if (isFanMemberRoute) {
              if (activityType === 'event') {
                navigate(navigateTo('qr-code'));
              } else {
                navigate(navigateTo('member'));
              }
            } else {
              // Regular dynamic route navigation
              if (activityType === 'event') {
                navigate(`/${activityType}/${id}/qr-code`);
              } else {
                navigate(`/${activityType}/${id}/member`);
              }
            }
          }
        }, 1500);
      } else {
        setError('Phone number not registered for this event');
      }
    } catch (err) {
      setError('Unable to verify registration. Please check your phone number and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen relative ${isFanMemberRoute ? brand.pageBg : 'bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50'}`}>
      {/* Success celebration effect */}
      {showSuccess && (
        <div className="absolute inset-0 pointer-events-none z-20">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className={`absolute w-3 h-3 bg-gradient-to-r ${isFanMemberRoute ? brand.confetti : 'from-blue-400 to-purple-400'} rounded-full animate-ping`}
              style={{
                left: `${20 + Math.random() * 60}%`,
                top: `${20 + Math.random() * 60}%`,
                animationDelay: `${Math.random() * 1}s`,
                animationDuration: '0.8s'
              }}
            />
          ))}
        </div>
      )}

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4 sm:p-8">
        <div className={`w-full max-w-md transform transition-all duration-500 ${isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-4 opacity-0 scale-95'
          }`}>
          <div className={`bg-white rounded-lg shadow-xl p-6 sm:p-8 border ${isFanMemberRoute ? brand.cardBorder : 'border-blue-200'}`}>
            {/* Header Section */}
            <div className="text-center mb-8">
              <div className={`w-16 h-16 ${isFanMemberRoute ? brand.iconBg : 'bg-gradient-to-br from-blue-500 to-purple-600'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
              </div>
              <h2 className={`text-2xl sm:text-3xl font-bold mb-2 ${isFanMemberRoute ? brand.titleGradient : 'bg-gradient-to-r from-blue-600 via-purple-600 to-purple-700 bg-clip-text text-transparent'}`}>
                Welcome Back
              </h2>
              <p className="text-slate-600 font-medium text-base sm:text-lg">
                Enter your phone number to access your registration
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 rounded-lg flex items-start gap-3 animate-fadeIn bg-red-50 text-red-700 border border-red-200">
                <div className="flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-sm">Verification Failed</p>
                  <p className="text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* Success Message */}
            {showSuccess && (
              <div className="mb-6 p-4 rounded-lg flex items-start gap-3 animate-fadeIn bg-green-50 text-green-700 border border-green-200">
                <div className="flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-sm">Login Successful</p>
                  <p className="text-sm">Redirecting you to your event dashboard...</p>
                </div>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="relative group">
                <label className="block text-slate-700 font-semibold text-sm mb-2">
                  Phone Number
                </label>
                <div className={`relative rounded-lg transition-all duration-300 bg-slate-50 border border-slate-300 ${isFanMemberRoute ? brand.inputFocus : 'group-focus-within:border-blue-400 group-focus-within:ring-2 group-focus-within:ring-blue-200'}`}>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="Enter your registered phone number"
                    required
                    className="w-full px-4 py-3 pl-12 bg-transparent outline-none text-slate-800 placeholder-slate-500 font-medium rounded-lg"
                  />
                  <FaPhone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !phoneNumber}
                className={`w-full py-3 rounded-lg font-semibold text-white transition-all duration-300 
                  disabled:opacity-70 disabled:cursor-not-allowed group relative overflow-hidden
                  ${isFanMemberRoute ? brand.button : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'}
                  transform hover:scale-105 shadow-lg hover:shadow-xl`}
              >
                <span className={`relative z-10 flex items-center justify-center gap-2 ${loading ? 'opacity-0' : 'opacity-100'
                  }`}>
                  {showSuccess ? 'Success!' : 'Login'}
                  {!showSuccess && <FaArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />}
                </span>
                {loading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <BeatLoader color="#ffffff" size={8} />
                  </div>
                )}
              </button>

              <div className="text-center">
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <p className="text-slate-600 text-sm font-medium">
                    Don't have an account?{' '}
                    <button
                      type="button"
                      onClick={() => {
                        if (isFanMemberRoute) {
                          navigate(navigateTo());
                        } else {
                          navigate(`/${activityType}/${id}/register`);
                        }
                      }}
                      className={`font-semibold transition-colors underline decoration-2 ${isFanMemberRoute ? `${brand.link} ${brand.linkDecoration}` : 'text-blue-600 hover:text-blue-700 decoration-blue-300 hover:decoration-blue-400'}`}
                    >
                      Register here
                    </button>
                  </p>
                </div>
              </div>
            </form>

            {/* Additional info */}
            <div className="mt-6 text-center">
              <p className="text-xs text-slate-500 font-medium">
                Secure login with phone number verification
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;